import React, { Fragment, useEffect, useState, useRef  } from 'react'
import {
  Box,
  Button,
  Flex,
  Tabs,
  TabList,
  Tab,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Spinner,
  Center
} from '@chakra-ui/react'
import { useQueryClient } from 'react-query'

import { DashboardHeader, withApp } from '@/features/Layout'
import { ToshiOsAddPointsLog } from '@/features/toshi_os_add_points_logs/types'
import { Meta } from '@/features/filters/types'

import { ToshiOsAddPointsLogTable } from './ToshiOsAddPointsLogTable'
import { Pagination } from '@/components/shared/Pagination'
import { useMeta } from '@/features/filters'
import { useToshiOsAddPointsLogs } from '@/features/toshi_os_add_points_logs/queries'

import { TOSHI_OS_ADD_POINTS_LOGS } from '@/features/toshi_os_add_points_logs/constants'
import { t } from '@/lib/helpers'

import { ToshiOsAddPointsLogFilterForm } from './ToshiOsAddPointsLogFilterForm'
import type { ToshiOsAddPointsLogFilterType } from './ToshiOsAddPointsLogFilterForm'

import { useRetryAll } from '@/features/toshi_os_add_points_logs/mutations'

type Props = {
  toshi_os_add_points_logs: ToshiOsAddPointsLog[]
  meta: Meta
}

const TAB_CONFIG = [
  { label: t('views.admin.toshi_os_add_points_logs.tabs.waiting'), value: 'waiting' },
  { label: t('views.admin.toshi_os_add_points_logs.tabs.done'), value: 'done' },
]

function ToshiOsAddPointsLogsPage(props: Props) {
  const { toshi_os_add_points_logs: initialLogs, meta: initialMeta } = props
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const { meta, setMeta, setMetas, setMetasForToshiOsAddPointsLog } =
    useMeta(initialMeta)

  const [tabIndex, setTabIndex] = useState(0)

  const currentTabValue = TAB_CONFIG[tabIndex].value

  const {
    data: toshi_os_add_points_logs,
    isFetching,
  } = useToshiOsAddPointsLogs({
    initialData: { data: initialLogs, meta: initialMeta },
    params: {
      ...meta,
      tab: currentTabValue === 'all' ? undefined : currentTabValue,
    },
  })

  const { mutateAsync: retryAll, isLoading: isRetryingAll } = useRetryAll()

  useEffect(() => {
    setMetas(toshi_os_add_points_logs.meta)
  }, [toshi_os_add_points_logs.meta])

  const handleSearch = (data: ToshiOsAddPointsLogFilterType) => {
    setMetasForToshiOsAddPointsLog({
      ...data,
      page: 1,
      tab: currentTabValue === 'all' ? undefined : currentTabValue,
    })
  }


  const handleConfirm = async () => {
    if (isRetryingAll) return;

    await retryAll();
    onClose();
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index)
    setMetasForToshiOsAddPointsLog({
      ...meta,
      page: 1,
      tab: TAB_CONFIG[index].value === 'all' ? undefined : TAB_CONFIG[index].value,
    })
  }

  return (
    <Fragment>
      <Box layerStyle="container">
        <DashboardHeader
          title={t('views.admin.toshi_os_add_points_logs.index.title')}
          addons={
            <HStack>
              <>
                <Button
                  onClick={onOpen}
                  isLoading={isRetryingAll}
                  isDisabled={isRetryingAll}
                  variant="outline"
                  sx={{
                    h: '48px',
                    minW: '120px',
                    color: '#8BC34A',
                    borderColor: '#8BC34A',
                  }}
                >
                  {t('views.admin.toshi_os_add_points_logs.index.retry_all')}
                </Button>

                <AlertDialog
                  isOpen={isOpen}
                  leastDestructiveRef={cancelRef}
                  onClose={onClose}
                  isCentered
                >
                  <AlertDialogOverlay>
                    <AlertDialogContent>
                      <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        確認
                      </AlertDialogHeader>

                      <AlertDialogBody>
                        ポイントサービスの復旧は確認済みですか？
                      </AlertDialogBody>

                      <AlertDialogFooter>
                        <Button
                          ref={cancelRef}
                          onClick={onClose}
                          variant="outline"
                          isDisabled={isRetryingAll}
                          sx={{
                            color: '#999999',
                            borderColor: '#dcdcdc',
                          }}
                        >
                          キャンセル
                        </Button>

                        <Button
                          ml={3}
                          onClick={handleConfirm}
                          isLoading={isRetryingAll}
                          isDisabled={isRetryingAll}
                        >
                          確認済み
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialogOverlay>
                </AlertDialog>
              </>

              <Button
                onClick={() =>
                  queryClient.invalidateQueries([TOSHI_OS_ADD_POINTS_LOGS])
                }
                isLoading={isFetching}
                isDisabled={isFetching}
                sx={{ h: '48px', minW: '100px' }}
              >
                {t('views.admin.toshi_os_add_points_logs.index.refresh')}
              </Button>
            </HStack>          }
        />

        <ToshiOsAddPointsLogFilterForm onSearch={handleSearch} />

        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          variant="line"
          colorScheme="green"
          mb="20px"
        >
          <TabList borderBottom="1px solid" borderColor="gray.200">
            {TAB_CONFIG.map((tab) => (
              <Tab
                key={tab.value}
                _selected={{
                  color: '#8BC34A',
                  borderColor: '#8BC34A',
                  fontWeight: '600',
                }}
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>
        </Tabs>

        <Box position="relative" sx={{ mb: '24px' }}>
          {isFetching && (
            <Center
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="whiteAlpha.700"
              zIndex="1"
            >
              <Spinner size="lg" />
            </Center>
          )}

          <ToshiOsAddPointsLogTable data={toshi_os_add_points_logs.data} />
        </Box>


        <Flex layerStyle="content" justifyContent="flex-end">
          <Pagination
            page={meta.page}
            per={meta.per}
            total={meta.total}
            onChangePage={(page) => setMeta('page', page)}
          />
        </Flex>
      </Box>
    </Fragment>
  )
}

export default withApp(ToshiOsAddPointsLogsPage)
